<template>
    <footer class="border-t border-slate-200 bg-white">
        <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div class="grid gap-10 md:grid-cols-4">
                <section aria-label="TinyCal summary">
                    <div class="flex items-center gap-2">
                        <Image
                            src="/favicon.svg"
                            alt=""
                            aria-hidden="true"
                            class="h-6 w-6 shrink-0 object-contain"
                        />
                        <span class="text-lg font-bold tracking-tight">
                            {{ appName }}
                        </span>
                    </div>
                    <p class="mt-3 text-sm text-slate-600">
                        Booking, reminders, and a polished client-facing page
                        for solo experts.
                    </p>
                </section>

                <nav aria-labelledby="footer-features-heading">
                    <h2
                        id="footer-features-heading"
                        class="text-sm font-bold text-slate-900"
                    >
                        Features
                    </h2>
                    <ul class="mt-3 space-y-2 text-sm text-slate-600">
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.flexibleAvailability"
                            >
                                Flexible Availability
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.flexibleScheduling"
                            >
                                Flexible Scheduling
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="
                                    marketingPageUrls.flexibleMeetingLocations
                                "
                            >
                                Flexible Meeting Locations
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.groupMeetings"
                            >
                                Group Meetings
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.recurringMeetings"
                            >
                                Recurring Meetings
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.integrations"
                            >
                                Integrations
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.payments"
                            >
                                Payments
                            </a>
                        </li>
                        <li>
                            <a
                                class="inline-flex items-center gap-1 hover:text-slate-900"
                                :href="marketingPageUrls.smsWhatsAppReminders"
                            >
                                <span>SMS/WhatsApp Reminders</span>
                                <span
                                    class="rounded-full border border-amber-200 bg-amber-50 px-2 py-0 text-[10px] font-bold uppercase tracking-wide text-amber-900"
                                >
                                    Upcoming
                                </span>
                            </a>
                        </li>
                        <li>
                            <a
                                class="inline-flex items-center gap-1 hover:text-slate-900"
                                :href="marketingPageUrls.automations"
                            >
                                <span>Automations</span>
                                <span
                                    class="rounded-full border border-amber-200 bg-amber-50 px-2 py-0 text-[10px] font-bold uppercase tracking-wide text-amber-900"
                                >
                                    Upcoming
                                </span>
                            </a>
                        </li>
                    </ul>
                </nav>

                <nav aria-labelledby="footer-company-heading">
                    <h2
                        id="footer-company-heading"
                        class="text-sm font-bold text-slate-900"
                    >
                        Company
                    </h2>
                    <ul class="mt-3 space-y-2 text-sm text-slate-600">
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.termsOfService"
                            >
                                Terms of Service
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.privacyPolicy"
                            >
                                Privacy Policy
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.refundPolicy"
                            >
                                Refund Policy
                            </a>
                        </li>
                        <li>
                            <a
                                :id="openCookieSettingsElementId"
                                class="hover:text-slate-900"
                                href="javascript:void(0)"
                                role="button"
                            >
                                Cookie Preferences
                            </a>
                        </li>
                    </ul>
                </nav>

                <nav aria-labelledby="footer-resources-heading">
                    <h2
                        id="footer-resources-heading"
                        class="text-sm font-bold text-slate-900"
                    >
                        Resources
                    </h2>
                    <ul class="mt-3 space-y-2 text-sm text-slate-600">
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="blogHomePageUrl"
                                target="_blank"
                                rel="dofollow"
                            >
                                Blog
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="businessNameGeneratorUrl"
                            >
                                Business Name Generator
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                href="https://headwayapp.co/tinycal-changelog"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                            >
                                Changelog
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                href="https://tinycal.canny.io/feature-requests"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                            >
                                Feature Requests
                            </a>
                        </li>
                        <li>
                            <a
                                class="hover:text-slate-900"
                                :href="marketingPageUrls.zoomTutorial"
                            >
                                Zoom Tutorial
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>

            <div
                class="mt-10 flex flex-col gap-2 border-t border-slate-200 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between"
            >
                <p>© {{ currentYear }} {{ appName }}. All rights reserved.</p>
                <p>
                    Google Calendar™, Google Meet™, and Google Analytics™ are
                    trademarks of Google LLC. Outlook™ is a trademark of
                    Microsoft.
                </p>
            </div>
        </div>
    </footer>
</template>

<script setup lang="ts">
    import Image from '@/Shared/Component/Image.vue'
    import {
        RoutePathFreeToolEnum,
        RoutePathMarketingEnum,
    } from '@/Shared/AutoGenerated/RoutePathEnum'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    defineProps({
        appName: {
            type: String,
            required: true,
        },
        openCookieSettingsElementId: {
            type: String,
            required: true,
        },
    })

    const urlFactory = new UrlFactory()
    const blogHomePageUrl = urlFactory.makeBlogHomePageUrl()
    const businessNameGeneratorUrl = urlFactory.makeFreeToolRoutePageUrl(
        RoutePathFreeToolEnum.BusinessNameGenerator_StepStart,
    )
    const marketingPageUrls = {
        flexibleAvailability: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_FlexibleAvailability,
        ),
        flexibleScheduling: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_FlexibleScheduling,
        ),
        flexibleMeetingLocations: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_FlexibleMeetingLocations,
        ),
        groupMeetings: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_GroupMeetings,
        ),
        recurringMeetings: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_RecurringMeetings,
        ),
        integrations: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_Integrations,
        ),
        payments: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_Payments,
        ),
        smsWhatsAppReminders: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_SmsWhatsAppReminders,
        ),
        automations: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.Feature_Automations,
        ),
        termsOfService: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.TermsOfService,
        ),
        privacyPolicy: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.PrivacyPolicy,
        ),
        refundPolicy: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.RefundPolicy,
        ),
        zoomTutorial: urlFactory.makeMarketingRoutePageUrl(
            RoutePathMarketingEnum.ZoomTutorial,
        ),
    }
    const currentYear = new Date().getFullYear()
</script>
